import Vue from 'vue';

import { SettingsState } from '@/store';
import { upendCell, TableColumn } from '@/core/components/table';
import { date, localize } from '@/core/filters';
import { ProductFilter } from '../controls';
import { Product } from '@/dto';
import { formatDate } from '@/core/helpers/DateTime';
import { getTableLayoutFromUserSettings } from './tableHelpers';

const common = {
    // cssClass: 'red',
};

/**
 * * Equivalent to dateFilter(value, onlyDate = true)
 * @return column settings to display cell value as date string
 */
export function DATE(): Partial<TableColumn> {
    return {
        ...common,
        dataType: 'date',
        customizeText: cellInfo =>
                    cellInfo.value &&
                    (typeof cellInfo.value === 'string' ||
                        cellInfo.value instanceof Date)
                        ? formatDate(cellInfo.value, true)
                        : cellInfo.valueText || ''
    };
}
const valToDate = (rawVal: any): string =>
    rawVal && (typeof rawVal === 'string' || rawVal instanceof Date)
        ? date(rawVal, true)
        : rawVal || '';

/**
 * * Equivalent to dateFilter(value, onlyDate = false)
 * @return column settings to display cell value as date time string
 */
export function DATE_WITH_TIME(): Partial<TableColumn> {
    return {
        ...common,
        dataType: 'datetime',
        customizeText: cellInfo =>
                    cellInfo.value &&
                    (typeof cellInfo.value === 'string' ||
                        cellInfo.value instanceof Date)
                        ? formatDate(cellInfo.value, false)
                        : cellInfo.valueText || ''
    };
}

/**
 * * Equivalent to localizeFilter(value, key)
 * @param {string} key - extra translation key, that will be add before cell value
 * @return column settings to display `${key}_${cell.value}`,
 * passed throw localize filter.
 */
export function LOCALIZE_WITH_KEY(key?: string): Partial<TableColumn> {
    return {
        ...common,
        dataType: 'string',
        customizeText: (cellInfo): string =>
            cellInfo.value
                ? localize(
                      typeof cellInfo.value === 'string'
                          ? cellInfo.value
                          : String(cellInfo.value),
                      key
                  )
                : ''
    };
}

/**
 * Replace value from data[selector] with localizeFilter(value, key) method result
 * @param {string} key - extra translation key, that will be add before cell value
 * @param {string} selector - column name thats will be used for value reciving
 */
export function REPLACE_VALUE_WITH_LOCALIZED(
    key: string,
    selector: string
): Partial<TableColumn> {
    return {
        ...common,
        dataType: 'string',
        calculateCellValue: (cellInfo: { [key: string]: any }): string =>
            localize(
                typeof cellInfo[selector] === 'string'
                    ? cellInfo[selector]
                    : String(cellInfo[selector]),
                key
            )
    };
}

/**
 * * Equivalent to localizeFilter(value)
 * @return column settings to display cell value, passed throw localize filter
 */
export const LOCALIZE: Partial<TableColumn> = {
    ...LOCALIZE_WITH_KEY()
};

/**
 * @param {number} digits
 * @return column settings to display cell value as float number with passed precision
 */
export function FIXED_NUMBER(digits: number): Partial<TableColumn> {
    return {
        ...common,
        dataType: 'number',
        format: {
            type: 'fixedPoint',
            precision: digits
        }
    };
}

/**
 * @return column settings to display cell value as number, like quantity, count, etc
 */
export function NUMBER(): Partial<TableColumn> {
    return {
        ...common,
        dataType: 'number',
        format: SettingsState.contextSettings.global.formatNumber || ''
    };
}

/**
 * @return column settings to display cell value with currency formating
 */
export function CURRENCY(): Partial<TableColumn> {
    return {
        ...common,
        dataType: 'number',
        format: SettingsState.contextSettings.global.formatCurrency || ''
    };
}

/**
 * @return column settings to display cell value with cost formatted as currency
 */
export function COST(field: string): Partial<TableColumn> {
    return {
        ...common,
        dataType: 'number',
        calculateCellValue: (cellInfo: { [key: string]: any[] }) => {
            if (!cellInfo[field] || cellInfo[field].length === 0 || !cellInfo[field][0]) {
                return null;
            } else {
                return cellInfo[field][0].maximalValue;
            }
        },
        calculateDisplayValue: (cellInfo: { [key: string]: any[] }): string => {
            if (!cellInfo[field] || cellInfo[field].length === 0 || cellInfo[field]![0].maximalValue === null) {
                return '';
            } else {
                return cellInfo[field][0].maximalValue;
            }
        },
        format: SettingsState.contextSettings.global.formatCurrency || ''
    };
}

/**
 * @return column settings to display cell value with unit formatted to lower or upper case
 */
export function STRING(field: string,
                       stringCase: 'upper' | 'lower' = 'lower'): Partial<TableColumn> {
    return {
        ...common,
        dataType: 'string',
        calculateCellValue: (cellInfo: { [key: string]: any[] }) => {
            const value = getFieldValue(cellInfo, field);
            if (!value) {
                return null;
            } else {
                switch (stringCase) {
                    case 'lower': {
                        return value.toString().toLowerCase();
                    }
                    case 'upper': {
                        return value.toString().toUpperCase();
                    }
                    default: {
                        return value;
                    }
                }
            }
        }
    };
}

function getFieldValue(cellInfo: { [key: string]: any[] }, field: string): any {
    const values = field.split('.');
    const value = values[0];
    const cellValue = cellInfo[value];
    if (!cellValue) {
        return null;
    }
    const restValues = values.splice(0, 1);

    if (values.length > 0) {
        return getFieldValue(cellValue as any, values.join('.'));
    } else {
        return cellValue;
    }
}

/**
 * @return column settings to display cell value with weight formating
 */
export function WEIGHT(): Partial<TableColumn> {
    return {
        ...common,
        dataType: 'number',
        format: SettingsState.contextSettings.global.formatWeight || ''
    };
}

/**
 * @return column settings to display cell value with area-value formating
 */
export function AREA(): Partial<TableColumn> {
    return {
        ...common,
        dataType: 'number',
        format: SettingsState.contextSettings.global.formatArea || ''
    };
}

/**
 * @return column settings to display cell value with volume-value formating
 */
export function VOLUME(): Partial<TableColumn> {
    return {
        ...common,
        dataType: 'number',
        format: SettingsState.contextSettings.global.formatVolume || ''
    };
}

/**
 * @param {function} componentFabricFunction
 * takes entire rowData and, based on it,
 * can return a Vue component that will be rendered in cell.
 * @return column settings to render component,
 * that may be return from passed as prop function in cell.
 */
export function COMPONENT<RowDataType>(
    componentFabricFunction: (rowData: RowDataType) => Vue | undefined
): Partial<TableColumn> {
    return {
        ...common,
        cellTemplate: (cell, cellInfo) => {
            const component = componentFabricFunction(cellInfo.data);

            if (component) {
                upendCell(cell, component);
            }
        }
    };
}

/**
 * @param {string} keyField
 * Field that is used as key field in DataSource.
 * @param {string} valueField
 * Field that is used as value field in DataSource.
 * @param {any} dataSource
 * DataSource for lookUp column.
 */
export function FILTER(keyField: string,
                       valueField: string,
                       dataSource: any[]): Partial<TableColumn> {
        return {
            ...common,
            dataType: 'string',
            calculateFilterExpression(value, operation) {
                const column = this;
                if (!dataSource) {
                    return '';
                }
                const emptyOnly = value === null;
                value = dataSource!.find(x => x.id === value);
                value = value ? value : '';
                if (value) {
                    const selector = (data: any) => {
                        const values = column.calculateCellValue!(data);
                        return values && values.indexOf(value[keyField]) >= 0;
                    };
                    return [selector, operation || '=', true];
                } else {
                    if (emptyOnly) {
                        const selector = (data: any) => {
                            const values = column.calculateCellValue!(data);
                            return values === null;
                        };
                        return [selector, operation || '=', true];
                    }
                }
                return '';
            },
            lookup: {
                dataSource: {
                    store: {
                        type: 'array',
                        data: dataSource,
                        key: keyField
                    }
                },
                valueExpr: keyField,
                allowClearing: true,
                displayExpr: valueField,
            }
        };
    }

/**
 * @param {string} property
 * CellProperty that stores array of objects.
 * @param {string} keyField
 * Field that is used as key field in DataSource.
 * @param {string} valueField
 * Field that is used as value field in DataSource.
 * @param {any} dataSource
 * DataSource for lookUp column.
 */
export function ARRAY(property: string,
                      keyField: string,
                      valueField: string,
                      dataSource: any[]): Partial<TableColumn> {
    return {
        ...common,
        dataType: 'string',
        calculateCellValue: (cellInfo: { [key: string]: any[] }) => {
            return cellInfo[property] ?
                cellInfo[property]?.map(x => dataSource.find(obj => obj[keyField] === x)[keyField]) : null;
        },
        calculateDisplayValue: (cellInfo: { [key: string]: any[] }): string => {
            return cellInfo[property] ?
                cellInfo[property].map(x => dataSource.find(obj => obj[keyField] === x)[valueField]).join(', ') : '';
        },
        calculateFilterExpression(value, operation) {
            const column = this;
            if (!dataSource) {
                return '';
            }
            const emptyOnly = value === null;
            value = dataSource!.find(x => x.id === value);
            value = value ? value : '';
            if (value) {
                const selector = (data: any) => {
                    const values = column.calculateCellValue!(data);
                    return values && values.indexOf(value[keyField]) >= 0;
                };
                return [selector, operation || '=', true];
            } else {
                if (emptyOnly) {
                    const selector = (data: any) => {
                        const values = column.calculateCellValue!(data);
                        return values === null;
                    };
                    return [selector, operation || '=', true];
                }
            }
            return '';
        },
        lookup: {
            dataSource: {
                store: {
                    type: 'array',
                    data: dataSource,
                    key: keyField
                }
            },
            valueExpr: keyField,
            allowClearing: true,
            displayExpr: valueField,
        }
    };
}

/**
 * @param {string} dataField name of product object in a row
 * @return column settings to render ProductFilter component.
 * _____
 * declared as separate template because it is very often used
 */
export function PRODUCT(dataField: string): Partial<TableColumn> {
    return {
        calculateCellValue: rowData => {
            const product: Product = dataField !== '' ? rowData[dataField] : rowData;

            return product ? product.name : '';
        },
        ...COMPONENT<any>((rowData: any) => {
            const product: Product = dataField !== '' ? rowData[dataField] : rowData;

            if (product) {
                return new ProductFilter({
                    propsData: {
                        product: dataField !== '' ? rowData[dataField] : rowData
                    }
                });
            }
        }),
        allowFiltering: true,
        allowSearch: true,
        allowSorting: true
    };
}

/** Disable column sorting, filtering, search e.t.c. */
export function NO_INTERACT(): Partial<TableColumn> {
    return {
        allowEditing: false,
        allowFiltering: false,
        allowGrouping: false,
        allowSearch: false,
        allowSorting: false,
        allowExporting: false
    };
}

export function SORT_GROUP_FILTER_EXPORT(): Partial<TableColumn> {
    return {
        allowEditing: false,
        allowFiltering: true,
        allowGrouping: true,
        allowSearch: true,
        allowSorting: true,
        allowExporting: true
    };
}

export function ONLY_EXPORT(): Partial<TableColumn> {
    return {
        allowEditing: false,
        allowFiltering: false,
        allowGrouping: false,
        allowSearch: false,
        allowSorting: false,
        allowExporting: true
    };
}

/**
 * @param {array} dataSource
 * @return column settings to render in cell dropdown
 * with passed dataSource as options.
 */
export function LOOKUP(dataSource: any[]): Partial<TableColumn> {
    return {
        ...common,
        width: 300,
        allowEditing: true,
        editorOptions: {
            placeholder: '',
            noDataText: ''
        },
        lookup: {
            allowClearing: true,
            dataSource
        }
    };
}
